import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from '@/hooks/useToast';
import {
  useDeleteCardCreditBySerial,
  CardCreditDTO,
  getListCardCreditsQueryKey,
} from '@/api/trafikportalen';
import Button from '../core/Button';
import { IconTrash } from '@/icons';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  card: CardCreditDTO;
  onOpenChange: (open: boolean) => void;
}

export const CreditCardDeleteForm = ({ card, onOpenChange }: Props) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const deleteCardByID = useDeleteCardCreditBySerial();

  const handleDelete = useCallback(() => {
    deleteCardByID.mutate(
      {
        cardSerial: card.card_serial,
      },
      {
        onSuccess: async () => {
          toast.success(
            {
              title: formatMessage({
                defaultMessage: `Kortet har tagits bort från resenären`,
              }),
            },
            { autoClose: 3000 },
          );

          await queryClient.invalidateQueries({
            queryKey: getListCardCreditsQueryKey(),
          });

          onOpenChange(false);
        },
        onError: (error) => {
          console.error('Error', error);
          toast.error(
            {
              title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
              description: error.message,
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [card, deleteCardByID, formatMessage, onOpenChange, queryClient]);

  return (
    <div>
      <p>
        <FormattedMessage defaultMessage="Är du säker på att du vill ta bort kortet från resenären?" />
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          <FormattedMessage defaultMessage="Avbryt" />
        </Button>
        <Button
          color="red"
          type="submit"
          variant="filled"
          startIcon={<IconTrash />}
          onClick={handleDelete}
          loading={deleteCardByID.isPending}
        >
          <FormattedMessage defaultMessage="Radera" />
        </Button>
      </div>
    </div>
  );
};
